import {
    FaArrowDown,
    FaArrowUp,
    FaDownload, FaEdit,
    FaHistory, FaLayerGroup, FaRegClock,
    FaTrash
} from "react-icons/fa";
import {FaBorderAll, FaFilePen, FaTableList} from "react-icons/fa6";
import Swal from "sweetalert2";
import {activateLatestPreparedAction} from "../../../../utils/action-feedback.js";
import LibraryService from "../../../../services/web/library-service.js";
import SFolder from "../../../../components/icons/folder.jsx";
import SSentryDoc from "../../../../components/icons/sentry-doc.jsx";
import SFile from "../../../../components/icons/file.jsx";
import SPdf from "../../../../components/icons/pdf.jsx";
import SPicture from "../../../../components/icons/picture.jsx";

export const iconsByType = {
    FOLDER: SFolder,
    DOCUMENT: SSentryDoc,
    FILE: SFile,
}

export const getIconByType = (item) => {
    if (!item) return SFile;

    const { type, attachExtension } = item;

    if (type === "FOLDER") {
        return SFolder;
    }

    if (type === "DOCUMENT") {
        return SSentryDoc;
    }

    if (type === "FILE") {
        const extension = attachExtension?.toLowerCase();

        if (extension === "pdf") {
            return SPdf;
        }

        if (imageExtensions.includes(extension)) {
            return SPicture;
        }

        return SFile;
    }

    return SFile;
};

const iconColorByType = {
    FOLDER: "text-[#CC9520]",
    DOCUMENT: "text-[#396694]",
    FILE: "text-[#396694]",
    PDF: "text-[#B8281B]",
    PICTURE: "text-[#B8281B]",
}

export const getIconColorByType = (item) => {
    if (!item) return iconColorByType.FILE;

    const extension = item.attachExtension?.toLowerCase();

    if (item.type === "FILE" && extension === "pdf") {
        return iconColorByType.PDF;
    }

    if (item.type === "FILE" && extension && imageExtensions.includes(extension)) {
        return iconColorByType.PICTURE;
    }

    return iconColorByType[item.type] ?? iconColorByType.FILE;
};

export const libraryTypeLabels = {
    FOLDER: "Pasta",
    DOCUMENT: "Sentry Doc",
    FILE: "Arquivo",
}

export const orderOptions = [
    { value: "name_asc", label: "Nome A-Z", icon: FaArrowUp },
    { value: "name_desc", label: "Nome Z-A", icon: FaArrowDown },
    { value: "created_at_desc", label: "Mais recentes", icon: FaRegClock },
    { value: "created_at_asc", label: "Mais antigos", icon: FaHistory },
    { value: "updated_at_desc", label: "Recém alterados", icon: FaEdit },
];

export const typeOptions = [
    { value: "all", label: "Todos", icon: FaLayerGroup },
    { value: "FOLDER", label: "Pastas", icon: SFolder, iconSize: 16 },
    { value: "DOCUMENT", label: "Sentry Doc", icon: SSentryDoc, iconSize: 16 },
    { value: "FILE", label: "Arquivos", icon: SFile, iconSize: 16 },
];

export const viewModeOptions = [
    { key: "list", label: "Tabela", Icon: FaTableList },
    { key: "grid", label: "Grade", Icon: FaBorderAll },
];

export function buildLibraryButtonOptions({ onRename, onDownload, onDelete }) {
    const options = [];

    if (onRename) {
        options.push({
            label: "Renomear",
            icon: FaFilePen,
            onClick: onRename,
        });
    }

    if (onDownload) {
        options.push({
            label: "Baixar",
            icon: FaDownload,
            disabled: (item) => item?.type === "DOCUMENT" || item?.type === "FOLDER",
            onClick: onDownload,
        });
    }

    if (onDelete) {
        options.push({
            label: "Excluir",
            icon: FaTrash,
            danger: true,
            actionFeedbackType: "delete",
            deferActionFeedback: true,
            successText: (item) => getLibrarySuccessMessage(item.type, "delete"),
            onClick: onDelete,
        });
    }

    return options;
}

export function newItemsButtonOptions({ onNewFolder, onNewDoc, onNewFile }) {
    const options = [];
    if (onNewFolder) {
        options.push({
            label: "Pasta",
            icon: SFolder,
            iconSize: 16,
            onClick: onNewFolder,
        });
    }

    if (onNewFile) {
        options.push({
            label: "Arquivo",
            icon: SFile,
            iconSize: 16,
            onClick: onNewFile,
        });
    }

    if (onNewDoc) {
        options.push({
            label: "Sentry Doc",
            icon: SSentryDoc,
            iconSize: 16,
            onClick: onNewDoc,
        });
    }

    return options;
}

const getLibraryTypeText = (item) => {
    const config = libraryTypeConfig[item.type];

    if (!config) return "o item";

    const article = config.gender === "female" ? "A" : "O";
    const deletedLabel = config.gender === "female" ? "excluída" : "excluído";

    return `
                ${article} ${config.label.toLowerCase()} "${item.name}" será ${deletedLabel} permanentemente.
                Essa ação não poderá ser desfeita.
                <br/>Para confirmar, digite <b>excluir</b> abaixo:
            `;
}

const showDeleteConfirmationLoading = () => {
    const confirmButton = Swal.getConfirmButton();

    Swal.disableButtons();

    if (!confirmButton) return;

    confirmButton.classList.add("bg-[#9B9B9B]", "!opacity-100", "!flex", "!items-center", "!justify-center", "!gap-1");
    confirmButton.style.setProperty("background-color", "#9B9B9B", "important");
    confirmButton.setAttribute("aria-busy", "true");
    confirmButton.innerHTML = '<span class="inline-block size-5 rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] animate-spin text-gray-300"></span> Excluindo...';
};

export async function confirmDeleteItem(item, onConfirm) {
    const html = getLibraryTypeText(item);

    const result = await Swal.fire({
        title: "Atenção",
        icon: "warning",
        html,
        input: "text",
        confirmButtonText: "Confirmar",
        confirmButtonColor: "#063A6C",
        cancelButtonText: "Cancelar",
        showCancelButton: true,
        reverseButtons: true,
        preConfirm(inputValue) {
            if (inputValue.trim() !== "excluir") {
                Swal.showValidationMessage("Digite corretamente a palavra-chave");
                return false;
            }

            activateLatestPreparedAction();
            showDeleteConfirmationLoading();

            return onConfirm?.();
        },
    });

    return result.isConfirmed;
}

export async function confirmDeleteItems(items, onConfirm) {
    const result = await Swal.fire({
        title: "Atenção",
        icon: "warning",
        html: `
            ${items.length} ${items.length === 1 ? "item será excluído" : "itens serão excluídos"}.<br/><br/>
            Caso exista alguma pasta selecionada, todo o seu conteúdo também será perdido.<br/><br/>
            <br/>Para confirmar, digite <b>excluir</b> abaixo:
        `,
        input: "text",
        confirmButtonText: "Confirmar",
        confirmButtonColor: "#063A6C",
        cancelButtonText: "Cancelar",
        showCancelButton: true,
        reverseButtons: true,
        preConfirm(inputValue) {
            if (inputValue.trim() !== "excluir") {
                Swal.showValidationMessage("Digite corretamente a palavra-chave");
                return false;
            }

            activateLatestPreparedAction();
            showDeleteConfirmationLoading();

            return onConfirm?.();
        },
    });

    return result.isConfirmed;
}

export const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "avif", "heic",];

const getImageMimeType = (extension) => {
    switch (extension) {
        case "jpg":
        case "jpeg":
            return "image/jpeg";
        case "png":
            return "image/png";
        case "gif":
            return "image/gif";
        case "svg":
            return "image/svg+xml";
        case "webp":
            return "image/webp";
        default:
            return "image/png";
    }
}

export const openLibraryFile = async (item) => {
    if (!item?.attachId) return;

    const extension = item.name?.split(".").pop()?.toLowerCase();

    const isPdf = extension === "pdf";
    const isImg = imageExtensions.includes(extension);

    if (isPdf) {
        window.open(LibraryService.openPDF(item.attachId), "_blank");
        return;
    }

    if (isImg) {
        const newTab = window.open("", "_blank");

        const response = await fetch(LibraryService.downloadFile(item.attachId));
        const blob = await response.blob();

        const imageBlob = new Blob([blob], {
            type: getImageMimeType(extension),
        });

        const imageUrl = URL.createObjectURL(imageBlob);

        if (newTab) {
            newTab.location.href = imageUrl;
        } else {
            window.open(imageUrl, "_blank");
        }

        return;
    }

    window.open(LibraryService.downloadFile(item.attachId), "_blank");
}

export const libraryErrorMessages = {
    //Genéricos
    "library.item.not.found": "Item não encontrado.",
    "library.item.not.folder": "O item selecionado não é uma pasta.",
    "library.item.invalid.type": "Tipo de arquivo inválido.",
    "library.item.delete.confirmation.required": "A palavra de confirmação é obrigatória.",
    "library.parent.must.be.folder": "A pasta de destino precisa ser uma pasta válida.",
    //Pastas
    "library.folder.not.found": "Pasta não encontrada.",
    "library.folder.name.required": `O campo "Nome da pasta" é obrigatório.`,
    "library.folder.parent.child.folder": "Não é possível mover uma pasta para dentro de uma de suas subpastas.",
    "library.folder.name.duplicated": "Já existe uma pasta com esse nome nesta pasta.",
    "library.folder.has.children": "Esta pasta possui itens dentro dela. Confirme a exclusão para continuar.",
    "library.folder.parent.same.folder": "Não é possível mover uma pasta para dentro dela mesma.",
    "library.folder.parent.not.found": "A pasta de destino não foi encontrada.",
    "library.folder.parent.not.folder": "A pasta de destino precisa ser uma pasta válida.",
    "library.folder.max.depth": "O limite de subpastas da biblioteca é de 5.",
    "library.folder.must.be.empty": "A pasta precisa estar vazia para ser excluída",
    //Arquivos
    "library.file.name.required": `O nome do arquivo é obrigatório.`,
    "library.file.name.duplicated": "Já existe um arquivo com esse nome nesta pasta.",
    "library.file.empty": "Selecione um arquivo para continuar.",
    "library.file.parent.required": "Não é possível salvar arquivos na raiz.",
    "library.file.attach.not.found": "Anexo do arquivo não foi encontrado.",
    "library.file.max.size.exceeded": "Tamanho máximo de arquivo excedido.",
    //Sentry Doc
    "library.document.name.required": `O campo "Nome" é obrigatório.`,
    "library.document.name.already.used": "Já existe um Sentry Doc com esse nome nesta pasta.",
    "library.document.not.found": "Sentry Doc não encontrado.",
    "library.document.missing.name": `O campo "Nome" é obrigatório.`,
    "library.document.parent.required": "Não é possível salvar Sentry Doc na raiz.",
}

export function getLibraryErrorMessage(error, fallbackMessage = "Erro ao processar solicitação.") {
    const errorKey = getErrorKey(error);
    return libraryErrorMessages[errorKey] ?? fallbackMessage;
}

function getErrorKey(error) {
    const data = error.response?.data;

    if (!data) return null;

    if (typeof data === "string") {
        return data.trim();
    }

    return (
        data.message?.trim?.() ||
        data.error?.trim?.() ||
        data.code?.trim?.() ||
        null
    );
}

export function getFileType(extension) {
    const ext = extension?.toLowerCase();

    switch (true) {
        case !ext:
            return "Não informado";

        case imageExtensions.includes(ext):
            return "Imagem";

        case "pdf":
            return "PDF";

        default:
            return "Arquivo";
    }
}

export const libraryTypeConfig = {
    FOLDER: {
        label: "Pasta",
        nameInputLabel: "Nome da pasta *",
        gender: "female",
    },
    DOCUMENT: {
        label: "Sentry Doc",
        nameInputLabel: "Nome do SentryDoc *",
        gender: "male",
    },
    FILE: {
        label: "Arquivo",
        nameInputLabel: "Nome do arquivo *",
        gender: "male",
    },
}

export const getLibraryNameInputLabel = (type) => {
    return libraryTypeConfig[type]?.nameInputLabel ?? "Nome do item *";
}

export const getLibrarySuccessMessage = (type, action) => {
    if (action === "update") {
        return "Salvo com sucesso";
    }

    const config = libraryTypeConfig[type] || { label: "Item", gender: "male" };

    const actionLabels = {
        create: {
            male: "criado",
            female: "criada",
        },
        update: {
            male: "atualizado",
            female: "atualizada",
        },
        delete: {
            male: "excluído",
            female: "excluída",
        },
    }

    const actionLabel = actionLabels[action]?.[config.gender];

    if (!actionLabel) return "";

    return `${config.label} ${actionLabel} com sucesso`;
}